import React, { useContext, useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import mainStyle from "../styles/mainComponents.module.css";
import styles from "../styles/facturacion.module.css";
import { CompanyContext } from "../features/CompanyContext";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";

function Facturacion() {
  const { t } = useTranslation();
  const { selectedCompany } = useContext(CompanyContext);
  const { data, loading, error, execute } = useFetch();
  const [listado, setListado] = useState({ items: [], total_emitidas: 0, total_facturadas: 0, total_pendientes: 0 });
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingPersonaKey, setUpdatingPersonaKey] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [refEdit, setRefEdit] = useState("");
  const { execute: executePatch } = useFetch();
  const { execute: executePersona } = useFetch();
  const { data: detalleData, loading: detalleLoading, execute: fetchDetalle } = useFetch();

  const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";

  useEffect(() => {
    execute(`${apiUrl}/facturacion/getListadoYTotales`, "POST", {});
  }, [apiUrl]);

  useEffect(() => {
    if (data && data.items) {
      setListado({
        items: data.items,
        total_emitidas: data.total_emitidas ?? 0,
        total_facturadas: data.total_facturadas ?? 0,
        total_pendientes: data.total_pendientes ?? 0,
      });
    }
  }, [data]);

  const handleToggleFacturada = async (e, id_equipo_encuesta, currentFacturada) => {
    e.stopPropagation();
    const newFacturada = !currentFacturada;
    setUpdatingId(id_equipo_encuesta);
    try {
      await executePatch(`${apiUrl}/facturacion/marcarFacturada`, "PATCH", {
        id_equipo_encuesta,
        facturada: newFacturada,
      });
      await execute(`${apiUrl}/facturacion/getListadoYTotales`, "POST", {});
      if (expandedId === id_equipo_encuesta && detalle) {
        setDetalle((d) => (d ? { ...d, facturada: newFacturada } : null));
      }
    } catch (err) {
      console.error("Error al actualizar facturación:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRowClick = (row) => {
    if (expandedId === row.id_equipo_encuesta) {
      setExpandedId(null);
      setDetalle(null);
      return;
    }
    setExpandedId(row.id_equipo_encuesta);
    setRefEdit(row.referencia_factura ?? "");
    setDetalle(null);
    fetchDetalle(`${apiUrl}/facturacion/getDetalleFacturacion`, "POST", {
      id_equipo_encuesta: row.id_equipo_encuesta,
    });
  };

  useEffect(() => {
    if (detalleData && expandedId) {
      setDetalle(detalleData);
      setRefEdit(detalleData.referencia_factura ?? "");
    }
  }, [detalleData, expandedId]);

  const handleSaveReferencia = async (id_equipo_encuesta, currentFacturada) => {
    setUpdatingId(id_equipo_encuesta);
    try {
      await executePatch(`${apiUrl}/facturacion/marcarFacturada`, "PATCH", {
        id_equipo_encuesta,
        facturada: currentFacturada,
        referencia_factura: refEdit.trim() || null,
      });
      await execute(`${apiUrl}/facturacion/getListadoYTotales`, "POST", {});
      if (detalle) {
        setDetalle((d) => (d ? { ...d, referencia_factura: refEdit.trim() || null } : null));
      }
    } catch (err) {
      console.error("Error al guardar referencia:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTogglePersonaFacturada = async (e, id_equipo_encuesta, id_empleado, currentFacturada) => {
    e.stopPropagation();
    const key = `${id_equipo_encuesta}-${id_empleado}`;
    setUpdatingPersonaKey(key);
    try {
      await executePersona(`${apiUrl}/facturacion/marcarPersonaFacturada`, "POST", {
        id_equipo_encuesta,
        id_empleado,
        facturada: !currentFacturada,
      });
      await execute(`${apiUrl}/facturacion/getListadoYTotales`, "POST", {});
      if (expandedId === id_equipo_encuesta) {
        await fetchDetalle(`${apiUrl}/facturacion/getDetalleFacturacion`, "POST", {
          id_equipo_encuesta,
        });
      }
    } catch (err) {
      console.error("Error al marcar persona:", err);
    } finally {
      setUpdatingPersonaKey(null);
    }
  };

  return (
    <div className={mainStyle.main}>
      <SideBar selectedCompany={selectedCompany} />
      <div className={mainStyle.central}>
        <h1 className={`${mainStyle.titulo} ${styles.pageTitle}`}>{t("sidebar-facturacion")}</h1>
        <div className={styles.wrapper}>
            {loading && !listado.items.length ? (
              <div className={styles.loading}>{t("facturacion-cargando")}</div>
            ) : error ? (
              <div className={styles.empty}>{error.message || t("facturacion-error")}</div>
            ) : (
              <>
                <div className={styles.cards}>
                  <div className={styles.card}>
                    <div className={styles.cardLabel}>{t("facturacion-total-emitidas")}</div>
                    <div className={styles.cardValue}>{listado.total_emitidas}</div>
                  </div>
                  <div className={styles.card}>
                    <div className={styles.cardLabel}>{t("facturacion-total-facturadas")}</div>
                    <div className={styles.cardValue}>{listado.total_facturadas}</div>
                  </div>
                  <div className={styles.card}>
                    <div className={styles.cardLabel}>{t("facturacion-total-pendientes")}</div>
                    <div className={styles.cardValue}>{listado.total_pendientes}</div>
                  </div>
                </div>
                <div className={styles.tableWrap}>
                  {listado.items.length === 0 ? (
                    <div className={styles.empty}>{t("facturacion-sin-datos")}</div>
                  ) : (
                    <>
                      <div className={styles.tableSectionTitle}>
                        {t("facturacion-tabla-detalle")}
                      </div>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ width: "2rem" }} aria-hidden="true" />
                            <th>{t("facturacion-tabla-id")}</th>
                            <th>{t("facturacion-tabla-empresa")}</th>
                            <th>{t("facturacion-tabla-equipo")}</th>
                            <th className={`${styles.cellNum} ${styles.thRight}`}>{t("facturacion-tabla-personas")}</th>
                            <th className={`${styles.cellNum} ${styles.thRight}`}>{t("facturacion-tabla-facturadas")}</th>
                            <th className={`${styles.cellNum} ${styles.thRight}`}>{t("facturacion-tabla-pendientes")}</th>
                            <th>{t("facturacion-facturada")}</th>
                            <th>{t("facturacion-referencia")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listado.items.map((row) => (
                            <React.Fragment key={row.id_equipo_encuesta}>
                              <tr
                                className={`${row.facturada ? styles.rowFacturada : styles.rowPendiente} ${row.pendientes > 0 ? styles.rowConPendientes : ""} ${styles.rowClickable}`}
                                onClick={() => handleRowClick(row)}
                              >
                                <td className={styles.cellId} style={{ width: "2rem" }}>
                                  <span
                                    className={`${styles.chevron} ${expandedId === row.id_equipo_encuesta ? styles.chevronOpen : ""}`}
                                    aria-hidden="true"
                                  />
                                </td>
                                <td className={styles.cellId}>{row.id_encuesta}</td>
                                <td className={styles.cellEquipo}>{row.nombre_empresa ?? "—"}</td>
                                <td className={styles.cellEquipo}>
                                  <div className={styles.equipoCell}>
                                    <span>{row.nombre_equipo ?? "—"}</span>
                                    {(row.fecha_inicio || row.fecha_fin || row.estado_encuesta) && (
                                      <span className={styles.equipoSubline}>
                                        {row.fecha_inicio || row.fecha_fin ? (
                                          <>
                                            {row.fecha_inicio
                                              ? new Date(row.fecha_inicio).toLocaleDateString(undefined, {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "2-digit",
                                                })
                                              : "—"}
                                            {" – "}
                                            {row.fecha_fin
                                              ? new Date(row.fecha_fin).toLocaleDateString(undefined, {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "2-digit",
                                                })
                                              : "—"}
                                          </>
                                        ) : null}
                                        {row.estado_encuesta && (
                                          <>
                                            {(row.fecha_inicio || row.fecha_fin) && " · "}
                                            <span
                                              className={`${styles.estadoEncuesta} ${
                                                row.estado_encuesta === "activa"
                                                  ? styles.estadoActiva
                                                  : row.estado_encuesta === "programada"
                                                    ? styles.estadoProgramada
                                                    : styles.estadoFinalizada
                                              }`}
                                            >
                                              {row.estado_encuesta === "activa"
                                                ? t("facturacion-estado-activa")
                                                : row.estado_encuesta === "programada"
                                                  ? t("facturacion-estado-programada")
                                                  : t("facturacion-estado-finalizada")}
                                            </span>
                                          </>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className={styles.cellNum}>{row.numero_personas}</td>
                                <td className={styles.cellNum}>
                                  {row.facturada ? row.personas_facturadas : "—"}
                                </td>
                                <td
                                  className={`${styles.cellPendientes} ${row.pendientes > 0 ? styles.hasPendientes : ""}`}
                                >
                                  {row.pendientes}
                                </td>
                                <td className={styles.cellEstado} onClick={(e) => e.stopPropagation()}>
                                  <div className={styles.checkboxWrap}>
                                    <input
                                      type="checkbox"
                                      className={styles.checkbox}
                                      checked={!!row.facturada}
                                      disabled={updatingId === row.id_equipo_encuesta}
                                      onChange={(e) => handleToggleFacturada(e, row.id_equipo_encuesta, row.facturada)}
                                      aria-label={row.pendientes > 0 ? t("facturacion-pendiente") : (row.facturada ? t("facturacion-facturada") : t("facturacion-pendiente"))}
                                    />
                                    <span
                                      className={`${styles.badge} ${row.pendientes > 0 ? styles.badgePendiente : (row.facturada ? styles.badgeFacturada : styles.badgePendiente)}`}
                                    >
                                      {row.pendientes > 0 ? t("facturacion-pendiente") : (row.facturada ? t("facturacion-facturada") : t("facturacion-pendiente"))}
                                    </span>
                                  </div>
                                </td>
                                <td
                                  className={`${styles.ref} ${row.referencia_factura ? "" : styles.refEmpty}`}
                                  title={row.referencia_factura ?? ""}
                                >
                                  {row.referencia_factura ?? "—"}
                                </td>
                              </tr>
                              {expandedId === row.id_equipo_encuesta && (
                                <tr className={styles.detailRow}>
                                  <td colSpan={9} className={styles.detailCell}>
                                    <div className={styles.detailInner}>
                                      {detalleLoading ? (
                                        <div className={styles.detailLoading}>{t("facturacion-cargando")}</div>
                                      ) : detalle && detalle.id_equipo_encuesta === row.id_equipo_encuesta ? (
                                        <div className={styles.detailGrid}>
                                          <div className={styles.detailSection}>
                                            <div className={styles.detailSectionTitle}>
                                              {t("facturacion-personas-equipo")} ({detalle.miembros?.length ?? 0})
                                            </div>
                                            <ul className={styles.miembrosList}>
                                              {(detalle.miembros || []).map((m) => {
                                                const personaKey = `${row.id_equipo_encuesta}-${m.id_empleado}`;
                                                return (
                                                  <li key={m.id_empleado} className={styles.miembroRow} onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                      type="checkbox"
                                                      className={styles.checkbox}
                                                      checked={!!m.facturado}
                                                      disabled={updatingPersonaKey === personaKey}
                                                      onChange={(e) =>
                                                        handleTogglePersonaFacturada(
                                                          e,
                                                          row.id_equipo_encuesta,
                                                          m.id_empleado,
                                                          m.facturado
                                                        )
                                                      }
                                                      aria-label={m.facturado ? t("facturacion-facturada") : t("facturacion-pendiente-facturar")}
                                                    />
                                                    <span className={styles.miembroNombre}>
                                                      {[m.nombre, m.apellido].filter(Boolean).join(" ")}
                                                    </span>
                                                    {m.email && (
                                                      <span className={styles.miembroEmail}>({m.email})</span>
                                                    )}
                                                    <span
                                                      className={`${styles.badge} ${m.facturado ? styles.badgeFacturada : styles.badgePendiente}`}
                                                    >
                                                      {m.facturado
                                                        ? t("facturacion-facturada")
                                                        : t("facturacion-pendiente-facturar")}
                                                    </span>
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                            {row.facturada && (
                                              <p className={styles.detailSectionTitle} style={{ marginTop: "0.75rem", border: 0, padding: 0 }}>
                                                {t("facturacion-personas-facturadas-count")}: {detalle.personas_facturadas}
                                              </p>
                                            )}
                                          </div>
                                          <div className={styles.detailSection}>
                                            <div className={styles.detailSectionTitle}>{t("facturacion-referencia")}</div>
                                            <div className={styles.refForm}>
                                              <input
                                                type="text"
                                                className={styles.refInput}
                                                value={refEdit}
                                                onChange={(e) => setRefEdit(e.target.value)}
                                                placeholder={t("facturacion-referencia-placeholder")}
                                              />
                                              <button
                                                type="button"
                                                className={styles.refSaveBtn}
                                                disabled={updatingId === row.id_equipo_encuesta}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSaveReferencia(row.id_equipo_encuesta, row.facturada);
                                                }}
                                              >
                                                {t("save")}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}

export default Facturacion;
